import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrMetadata } from '@nostrify/nostrify';

export interface UserSearchResult {
  pubkey: string;
  metadata: NostrMetadata;
}

/**
 * Search for Nostr users by name/NIP-05 using NIP-50 search.
 * Debounce the query string before passing it in.
 */
export function useUserSearch(query: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['user-search', query],
    queryFn: async ({ signal }) => {
      if (!query || query.length < 2) return [];

      const events = await nostr.query([{
        kinds: [0],
        search: query,
        limit: 8,
      }], { signal });

      const results: UserSearchResult[] = [];
      const seen = new Set<string>();

      for (const event of events) {
        if (seen.has(event.pubkey)) continue;
        seen.add(event.pubkey);

        try {
          const metadata: NostrMetadata = JSON.parse(event.content);
          results.push({ pubkey: event.pubkey, metadata });
        } catch {
          // skip malformed profiles
        }
      }

      return results;
    },
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}
